import { getRepubMetaFromRepub } from '@cx-web/utils';

import { type Repub } from '@cx-modules/constants';

import { getPathAndRepubFromUrlPath } from '~/utils/getPathAndRepubFromUrlPath';

import arrow from './icons/arrow.svg';
import home from './icons/home.svg';
import styles from './Breadcrumbs.module.css';

type DataSchema = {
  '@context': 'https://schema.org';
  '@type': 'BreadcrumbList';
  itemListElement: {
    '@type': 'ListItem';
    item: {
      '@id': string;
      '@type': 'WebPage';
      name: string;
    };
    position: number;
  }[];
};
interface BreadcrumbsProps {
  dataSchema: DataSchema;
  repub: Repub;
  requestUrl: string;
}

export const Breadcrumbs = ({ dataSchema, repub, requestUrl }: BreadcrumbsProps): JSX.Element => {
  const { repubPath: urlRepub } = getRepubMetaFromRepub(repub);
  // requestUrl is a fully-qualified URL on the server, but it round-trips through the loader JSON and
  // could arrive path-only on the client; the base keeps new URL() from throwing on relative input.
  const { path } = getPathAndRepubFromUrlPath(new URL(requestUrl, 'https://placeholder.invalid').pathname);
  const breadcrumbs = path.split('/').filter(Boolean);
  const json = JSON.stringify(dataSchema);

  return (
    <nav aria-label="breadcrumbs" className={styles.breadcrumbsContainer} data-testid="breadcrumbs">
      <div className={styles.breadcrumbsWrapper}>
        <ol className={styles.breadcrumbsOl}>
          <li className={styles.breadCrumbLi}>
            <a data-testid="home-anchor" href="./">
              <picture>
                <source srcSet={home} type="image/svg+xml" />
                <img alt="home" data-testid="home-image" height={14} src={home} width={14} />
              </picture>
            </a>
          </li>
          {breadcrumbs.map((breadcrumb, idx) => {
            const href = `${urlRepub}/${breadcrumbs.slice(0, idx + 1).join('/')}`;
            const isLast = idx === breadcrumbs.length - 1;
            const formattedBreadcrumb = breadcrumb.replace(/-/g, ' ');

            const arrowElement = (
              <span className={styles.arrowWrap}>
                <picture className={styles.arrow}>
                  <source srcSet={arrow} type="image/svg+xml" />
                  <img alt="arrow" data-testid="arrow-image" height={14} src={arrow} width={8} />
                </picture>
              </span>
            );

            const textElement = (
              <span className={styles.breadCrumbText} data-testid="breadcrumb-text">
                {formattedBreadcrumb}
              </span>
            );

            const breadCrumbLink = (
              <a
                className={styles.breadCrumbLink}
                data-testid="breadcrumb-link"
                href={href}
                title={formattedBreadcrumb}
              >
                {textElement}
              </a>
            );

            const lastBreadCrumb = <span className={styles.breadCrumbLast}>{textElement}</span>;

            return (
              <li className={styles.breadCrumbLi} key={href}>
                {arrowElement}
                {isLast ? lastBreadCrumb : breadCrumbLink}
              </li>
            );
          })}
        </ol>
      </div>
      {json && (
        <script
          dangerouslySetInnerHTML={{
            __html: json,
          }}
          type="application/ld+json"
        />
      )}
    </nav>
  );
};
